import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UsersService } from '../entity-services/users.service';
import { PermissionsUtilityService } from './permissions-utility.service';
import { mergeUserDataLevels } from '../../shared/utils/unwrap-user-data';
import { lastValueFrom } from 'rxjs';

// Define types for better readability and type safety.
interface LegalEntity {
  legalEntityNum: string;
  legalEntityName: string;
}

interface Contract {
  contractNum: string;
  contractType: string;
  departments?: string[];
  sites?: { siteNum: string }[];
  legalEntities?: (LegalEntity | string)[]; // Can be object or string
  databaseOrg: string;
}

export interface UserPermissions {
  role?: string;
  legalEntities?: (LegalEntity | string)[];
  contractTypes?: string[];
  departments?: string[];
  sites?: { siteNum: string }[];
  databaseOrganizations?: string[];
}

/**
 * A service for handling user permissions and filtering contracts based on those permissions.
 *
 * This service has been refactored to use modern Angular features like Signals for state
 * management and native JavaScript array methods for filtering. It also addresses the
 * design concern of keeping the service stateless for better testability and predictability.
 *
 * This version of the service no longer holds shared, mutable state in a public property.
 * Instead, it exposes a `permissions` signal that can be read by other components or services.
 * The `filterContracts` method is now a pure function, making it more reliable and reusable.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private usersService = inject(UsersService);
  private permissionsUtility = inject(PermissionsUtilityService);
  private http = inject(HttpClient);

  // Track the current user's email so the synchronous single-contract /
  // list filters below can honor the ownership override (see PermissionsUtilityService).
  private currentUserEmail: string | null = null;

  constructor() {
    this.usersService.currentUserEmail$.subscribe(email => this.currentUserEmail = email);
  }

  // Use a signal to hold the user's permissions. This is a reactive
  // way to manage state within the service.
  private _permissions = signal<UserPermissions | null>(null);

  // Expose the signal as a public getter for other components to read.
  public readonly permissions = this._permissions.asReadonly();

  /**
   * Updates a user's permissions via an HTTP PUT request.
   * @param userId The ID of the user to update.
   * @param permissions The permissions object to apply.
   * @returns A Promise that resolves when the update is complete.
   */
  async updateUserPermissions(userId: string, permissions: object): Promise<any> {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    const jsonReadyBody = {
      userId,
      permissions
    };

    //console.log('PermissionsService.updateUserPermissions() - request body:', jsonReadyBody);

    try {
      const response = await lastValueFrom(this.http.put(environment.URL_USERS_NT_UPDATE_PERMISSIONS, jsonReadyBody, options));
      //console.log('PermissionsService.updateUserPermissions() - response:', response);
      return response;
    } catch (error) {
      console.error('permissions.service | updateUserPermissions | update failed: ', error);
      throw error;
    }
  }

  /**
   * Fetches the permissions for a given user ID and updates the service's internal signal.
   * @param id The ID of the user.
   */
  async getUserPermissions(id: string): Promise<void> {
    try {
      const userPermissionsPayload = await lastValueFrom(this.usersService.getUserById(id.toLowerCase()));
      // Migration-safe: merge root/.userData/.userData.userData so fields split across
      // levels (role at root, scope arrays nested) are all captured.
      const permissions = mergeUserDataLevels(userPermissionsPayload);
      if (permissions) {
        this._permissions.set(permissions);
      } else {
        console.warn('This is a NEW user and was not found in the User list.');
        this._permissions.set(null);
      }
    } catch (error) {
      console.error('permissions.service | getUserPermissions | Failed to fetch permissions: ', error);
      this._permissions.set(null);
    }
  }

  /**
   * Fetches permissions for a specific user WITHOUT updating the service's internal signal.
   * Use this when you want to get/edit another user's permissions.
   * @param userId The ID of the user to fetch permissions for.
   * @returns A Promise that resolves to the user's permissions object or null.
   */
  async getUserPermissionsById(userId: string): Promise<UserPermissions | null> {
    try {
      const userPermissionsPayload = await lastValueFrom(this.usersService.getUserById(userId.toLowerCase()));

      //console.log('PermissionsService.getUserPermissionsById() - payload:', userPermissionsPayload);

      if (!userPermissionsPayload) {
        console.warn(`User ${userId} not found in DynamoDB. User may need to be created first.`);
        return null;
      }

      // Migration-safe: MERGE root / .userData / .userData.userData so fields split
      // across levels (role at root, scope arrays nested) are all captured. Picking a
      // single level dropped whichever fields lived at the other level.
      const permissions = mergeUserDataLevels<UserPermissions>(userPermissionsPayload);
      if (permissions) {
        return permissions;
      } else {
        console.warn(`User ${userId} found but has no permissions set yet.`);
        return null;
      }
    } catch (error) {
      console.error(`permissions.service | getUserPermissionsById | Failed to fetch permissions for user ${userId}:`, error);
      return null;
    }
  }

  /**
   * Checks if a single contract has permissions to be viewed by the current user.
   * @param contractObject The contract to check.
   * @returns A boolean indicating if the user has permissions to view the contract.
   */
  userHasPermissionsToViewContract(contractObject: Contract): boolean {
    const permissions = this.permissions();
    if (!permissions) {
      return false;
    }
    return this.filterContracts([contractObject], permissions).length > 0;
  }

  /**
   * Filters a list of contracts based on the provided user permissions.
   *
   * Delegates to the single canonical implementation in PermissionsUtilityService
   * (which also applies the owner/coordinator override) so this service can't drift
   * out of sync with the list-visibility pipeline. The current user's email is
   * supplied so the ownership override is honored here too.
   *
   * @param contractsFromServer The list of contracts to filter.
   * @param permissions The permissions object to use for filtering.
   * @returns The filtered list of contracts.
   */
  filterContracts(contractsFromServer: Contract[], permissions: UserPermissions): Contract[] {
    return this.permissionsUtility.filterContracts(
      contractsFromServer as any,
      permissions as any,
      this.currentUserEmail
    ) as unknown as Contract[];
  }
}